import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, OnDestroy, Renderer2, computed, effect, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as sass from 'sass';
import { HeaderData } from 'src/app/components/dynamic-header/category/header-data';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { CommunicationService } from 'src/app/core/services/communication/communication.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { ThemeConfigurationService } from 'src/app/core/services/theme-configuration/theme-configuration.service';
import { createDefaultHTMLElement, createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { ContactInfoPanelConfiguration, ThemeConfiguration } from 'src/app/home/components/dynamic-home-page/dynamic-home-page.component';
import { HomePageRenderService } from 'src/app/home/services/home-page-render.service';
import { LinkHandler } from 'src/app/shared/class/link-handler.class';
import { Category } from 'src/app/shared/services/common-api.service';
import { getRouteProperty } from '../../core/functions/route-signal.functions';
import { ContactInfoPanelComponent } from './category/contact-info-panel/contact-info-panel.component';
import { Layout1Component } from './category/layout-1/layout-1.component';
import { Layout10Component } from "./category/layout-10/layout-10.component";
import { Layout11Component } from "./category/layout-11/layout-11.component";
import { Layout12Component } from "./category/layout-12/layout-12.component";
import { Layout13Component } from "./category/layout-13/layout-13.component";
import { Layout2Component } from './category/layout-2/layout-2.component';
import { Layout3Component } from './category/layout-3/layout-3.component';
import { Layout4Component } from './category/layout-4/layout-4.component';
import { Layout5Component } from './category/layout-5/layout-5.component';
import { Layout6Component } from './category/layout-6/layout-6.component';
import { Layout7Component } from './category/layout-7/layout-7.component';
import { Layout8Component } from './category/layout-8/layout-8.component';
import { Layout9Component } from "./category/layout-9/layout-9.component";
import { MenuCollapseService } from './services/menu-collapse.service';

export enum DeviceType {
  desktop = "desktop",
  mobile = "mobile"
}

export enum HeaderLayout {
  headerLayout1 = "headerLayout1",
  headerLayout2 = "headerLayout2",
  headerLayout3 = "headerLayout3",
  headerLayout4 = "headerLayout4",
  headerLayout5 = "headerLayout5",
  headerLayout6 = "headerLayout6",
  headerLayout7 = "headerLayout7",
  headerLayout8 = "headerLayout8",
  headerLayout9 = "headerLayout9",
  headerLayout10 = "headerLayout10",
  headerLayout11 = "headerLayout11",
  headerLayout12 = "headerLayout12",
  headerLayout13 = "headerLayout13"
}

interface SearchSuggestionItem {
  suggestionKey: string;
  suggestionValue: string;
}

interface SearchQuery {
  keyboardEvent?: KeyboardEvent;
  suggestionItem?: SearchSuggestionItem;
  searchInputValue?: string;
}

@Component({
  selector: 'dynamic-header',
  templateUrl: './dynamic-header.component.html',
  styleUrls: ['./dynamic-header.component.scss'],
  host: { 'class': 'dynamic-theme-section' },
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    Layout1Component,
    Layout2Component,
    Layout3Component,
    Layout4Component,
    Layout5Component,
    Layout6Component,
    Layout7Component,
    Layout8Component,
    Layout9Component,
    Layout10Component,
    Layout11Component,
    ContactInfoPanelComponent,
    Layout12Component,
    Layout13Component
  ]
})
export class DynamicHeaderComponent extends LinkHandler implements AfterViewInit, OnDestroy {
  public override renderer = inject(Renderer2);
  public override elementRef = inject(ElementRef);
  public override router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  public override overlayService = inject(OverlayService);
  public override browserApiService = inject(BrowserApiService);

  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private themeConfigurationService = inject(ThemeConfigurationService);
  private cartRepositoryService = inject(CartRepositoryService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private menuCollapseService = inject(MenuCollapseService);
  private platformService = inject(PlatformService);
  private document = inject<Document>(DOCUMENT);
  private communicationService = inject(CommunicationService);
  private homePageRenderService = inject(HomePageRenderService);


  // Convert enums to signals for better reactivity
  readonly DeviceType = DeviceType;
  readonly HeaderLayout = HeaderLayout;

  // Convert all properties to signals
  selectedHeaderLayout = signal("");
  selectedDeviceType = signal<DeviceType>(this.deviceDetectionService.deviceType() === 'mobile' || this.deviceDetectionService.deviceType() === 'tablet' ? DeviceType.mobile : DeviceType.desktop);
  headerData = signal<HeaderData>(createDefaultHeaderData());

  // Use cart signal directly
  readonly cart = computed(() => {
    const cartValue = this.cartRepositoryService.cart();
    return cartValue;
  });

  // Convert boolean properties to signals
  dynamicHeaderInline = signal(false);
  dynamicHeaderHover = signal(false);

  // MutationObserver to watch for DOM changes
  private mutationObserver?: MutationObserver;


  isVisible = getRouteProperty('navbar', this.router, this.activatedRoute);

  layoutType = signal("");
  isBrowser = signal(false);

  // Use signals directly for better performance with OnPush
  readonly themeConfigurationSignal = this.themeConfigurationService.themeConfiguration;
  readonly bodyConfigurationsSignal = this.storeConfigurationRepositoryService.bodyConfigurations;

  // Direct access to HomePageRenderService for processed data (no duplicate parsing)
  readonly isHomePageRendered = this.homePageRenderService.isRendered;
  readonly headerDataFromRenderer = this.homePageRenderService.headerData;


  // Convert input to use new signal syntax
  clickedElementAvailable = input<HTMLElement | null>(createDefaultHTMLElement());

  // Convert outputs to use new signal syntax
  categoryHeaderStyle = output<string>();
  categoryHeaderStyle$ = output<string>();
  logout$ = output<boolean>();

  // Convert getter to computed signal with proper boolean return type
  readonly stickyHeader = computed(() => {
    const headerDataValue = this.headerData();
    const selectedDeviceTypeValue = this.selectedDeviceType();

    return Boolean(headerDataValue?.headerConfigurations &&
      ((selectedDeviceTypeValue === DeviceType.desktop && headerDataValue?.headerConfigurations?.desktopStickyHeader) ||
        (selectedDeviceTypeValue === DeviceType.mobile && headerDataValue?.headerConfigurations?.mobileStickyHeader)));
  });

  // Replace observable with computed signal
  readonly category = computed(() => this.bodyConfigurationsSignal()?.categories || []);

  // Convert to signal
  readonly storeContactDetails = computed(() => this.storeConfigurationRepositoryService.storeContactDetails());

  // Additional computed signals for better reactivity and performance
  readonly isThemeReady = computed(() => !!this.themeConfigurationSignal());
  readonly isHeaderReady = computed(() => !!this.headerDataFromRenderer());
  readonly isCartReady = computed(() => !!this.cart());
  readonly shouldShowStickyHeader = computed(() => this.stickyHeader() && this.isVisible());
  readonly currentDeviceType = computed(() => this.selectedDeviceType());
  readonly currentLayout = computed(() => this.selectedHeaderLayout());

  // Computed signal for determining which layout component to render
  readonly currentLayoutComponent = computed(() => {
    const layout = this.selectedHeaderLayout();
    switch (layout) {
      case HeaderLayout.headerLayout1: return Layout1Component;
      case HeaderLayout.headerLayout2: return Layout2Component;
      case HeaderLayout.headerLayout3: return Layout3Component;
      case HeaderLayout.headerLayout4: return Layout4Component;
      case HeaderLayout.headerLayout5: return Layout5Component;
      case HeaderLayout.headerLayout6: return Layout6Component;
      case HeaderLayout.headerLayout7: return Layout7Component;
      case HeaderLayout.headerLayout8: return Layout8Component;
      case HeaderLayout.headerLayout9: return Layout9Component;
      case HeaderLayout.headerLayout10: return Layout10Component;
      case HeaderLayout.headerLayout11: return Layout11Component;
      case HeaderLayout.headerLayout12: return Layout12Component;
      case HeaderLayout.headerLayout13: return Layout13Component;
      default: return null;
    }
  });

  // Computed signal for layout props to reduce template repetition
  readonly layoutProps = computed(() => ({
    headerData: this.headerData(),
    category: this.category(),
    clickedElementAvailable: this.clickedElementAvailable(),
    dynamicHeaderInline: this.dynamicHeaderInline(),
    dynamicHeaderHover: this.dynamicHeaderHover(),
    layoutType: this.layoutType(),
    cart: this.cart()
  }));

  constructor() {
    super();

    // Set browser state using already injected service
    this.isBrowser.set(this.platformService.isBrowser());

    // 1. STYLE EFFECTS FIRST - ensure styles are applied before data renders

    // Watch for HomePageRenderService completion - apply styles first
    effect(() => {
      const isRendered = this.isHomePageRendered();

      if (isRendered && this.isBrowser()) {
      }
    });

    // Theme configuration effect - apply theme styles
    effect(() => {
      const theme = this.themeConfigurationSignal();

      if (theme) {
        // Apply theme immediately
        this.applyThemeStyle(theme);
      }
    });

    // 2. DATA EFFECTS SECOND - render data after styles are ready

    // Header data effect - data comes directly from HomePageRenderService (no duplicate parsing)
    effect(() => {
      const header = this.headerDataFromRenderer();

      if (header) {
        // Apply data immediately after styles
        this.headerData.set(header);
        this.configureHeader(header);
        this.communicationService.setHeaderPosition(this.stickyHeader());
      }
    });

    // Clicked element effect
    effect(() => {
      const element = this.clickedElementAvailable();
      if (element) {
        this.handleClickedElementChange(element);
      }
    });

    // Device type effect
    effect(() => {
      const deviceType = this.deviceDetectionService.deviceType();
      if (deviceType === 'mobile' || deviceType === 'tablet') {
        this.selectedDeviceType.set(DeviceType.mobile);
      } else {
        this.selectedDeviceType.set(DeviceType.desktop);
      }
    });

    // Hamburger menu effect - MutationObserver handles this automatically
    effect(() => {
      const isHamburger = this.menuCollapseService.isHamburgerMenu();
      if (isHamburger && this.isBrowser()) {
      }
    });

    // Watch for visibility changes and re-initialize click actions
    effect(() => {
      const visible = this.isVisible();
      const layoutReady = this.selectedHeaderLayout();

      if (visible && layoutReady && this.isBrowser()) {
        // Delay slightly to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          this.checkForClickActions('menu');
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupClickActionHandlers();
  }

  // Old extraction methods removed - data now comes directly from HomePageRenderService

  // Old method removed - header data now comes directly from HomePageRenderService via signals

  private setupClickActionHandlers(): void {
    if (!this.isBrowser()) return;

    // Disconnect existing observer
    this.disconnectMutationObserver();

    // Set up MutationObserver to watch for DOM changes
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldRefresh = false;

      mutations.forEach((mutation) => {
        // Check if new anchor tags were added
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const hasNewAnchors = addedNodes.some(node =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).tagName === 'A'
          );
          if (hasNewAnchors) {
            shouldRefresh = true;
          }
        }

        // Check if attributes changed on existing anchor tags
        if (mutation.type === 'attributes' &&
          mutation.target.nodeType === Node.ELEMENT_NODE &&
          (mutation.target as Element).tagName === 'A') {
          const target = mutation.target as Element;
          if (target.hasAttribute('data-click_action_choice') ||
            target.hasAttribute('data-click_action_value')) {
            shouldRefresh = true;
          }
        }
      });

      if (shouldRefresh) {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          this.checkForClickActions('menu');
        });
      }
    });

    // Start observing the component's element
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-click_action_choice', 'data-click_action_value']
    });

    // Initial setup - pass 'menu' as page name for UTM tracking (utm_source=menu)
    this.checkForClickActions('menu');
  }

  applyThemeStyle(themeData: ThemeConfiguration) {
    const themeContainer = this.document.getElementsByClassName("dynamic-header-theme")[0];
    if (themeData?.themeFontFamily && themeContainer) {
      this.renderer.setStyle(themeContainer, 'font-family', themeData.themeFontFamily);
    };
    const inputContainer = this.document.getElementsByClassName("search-input")[0];
    if (inputContainer) {
      this.renderer.setStyle(inputContainer, 'font-family', themeData.themeFontFamily);
    };
  }


  ngOnDestroy(): void {
    // Disconnect MutationObserver
    this.disconnectMutationObserver();

    this.destroy();
  }

  private disconnectMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }
  }


  configureHeader(value: HeaderData) {

    this.selectedHeaderLayout.set(value.headerLayout);

    this.manageContactInfoPanel();

    switch (value.dynamicCategoryHeaderConfig.dynamicHeaderType) {
      case "dynamicHeaderInline":
        this.dynamicHeaderHover.set(false);
        this.dynamicHeaderInline.set(true);
        this.layoutType.set(value.dynamicCategoryHeaderConfig.layoutType);
        this.categoryHeaderData();
        break;
      case "dynamicHeaderHover":
        this.dynamicHeaderInline.set(false);
        this.dynamicHeaderHover.set(true);
        this.layoutType.set(value.dynamicCategoryHeaderConfig.layoutType);
        this.categoryHeaderData();
        break;
      case "dynamicHeaderRemove":
        this.dynamicHeaderInline.set(false);
        this.dynamicHeaderHover.set(false);
        this.layoutType.set(value.dynamicCategoryHeaderConfig.layoutType);
        break;
      default:
        this.categoryHeaderData();
    }

  }

  headerStyle(style: string) {
    this.categoryHeaderStyle.emit(style.toString());
  }

  categoryHeaderData() {
  }

  filterActiveSubcategories(subCategories: Category[] | undefined): Category[] {
    if (!subCategories || subCategories.length === 0) {
      return [];
    }

    return subCategories.filter(subCat => {
      if (subCat) {
        subCat.sub_categories = this.filterActiveSubcategories(subCat.sub_categories);
        return true;
      }
      return false;
    });
  }

  searchProducts(searchQuery?: SearchQuery | Event) {
    // Handle case where no parameter is passed or Event is passed
    if (!searchQuery || searchQuery instanceof Event) {
      return;
    }

    const { keyboardEvent, suggestionItem, searchInputValue } = searchQuery;

    if (keyboardEvent) {
      // Handle keyboard events (e.g., Enter key)
      if (keyboardEvent.key === 'Enter') {
        // âœ… Get search input value from the event target
        const inputValue = (keyboardEvent.target as HTMLInputElement)?.value || '';
        this.router.navigate([`/search`], { queryParams: { q: inputValue, utm_source: 'search', utm_medium: inputValue } });
      }
    } else if (suggestionItem) {
      const queryParams = {
        q: '', // Empty search term like the real site
        utm_source: 'search',
        utm_medium: '', // Empty medium like the real site
        [suggestionItem.suggestionKey]: suggestionItem.suggestionValue
      };
      this.router.navigate([`/search`], { queryParams });
    } else if (searchInputValue) {
      this.router.navigate([`/search`], { queryParams: { q: searchInputValue, utm_source: 'search', utm_medium: searchInputValue } });
    }
  }

  /* Adding top bar style if showTopBar is true */
  manageContactInfoPanel() {
    const contactInfoPanelConfiguration: ContactInfoPanelConfiguration | undefined = this.headerData()?.contactInfoPanel;

    if (contactInfoPanelConfiguration?.showDesktopContactInfoPanel || contactInfoPanelConfiguration?.showMobileContactInfoPanel) {
      let { style } = contactInfoPanelConfiguration;
      style = this.convertScssToCss(style) ?? "";
      /* If device type is mobile then updating mediaquery */
      let existingStyleTag;
      const existingStyleTags = this.document?.head?.querySelectorAll('style');
      if (existingStyleTags?.length) {
        for (let i = 0; i < existingStyleTags.length; i++) {
          if (existingStyleTags[i]?.getAttribute('id') === 'style-contact-info-panel') {
            existingStyleTag = existingStyleTags[i];
            break;
          }
        }
      }
      if (existingStyleTag) {
        this.renderer.setProperty(existingStyleTag, 'textContent', style);
      } else {
        const styleElement = this.renderer.createElement('style');
        styleElement.id = 'style-contact-info-panel';
        const text = this.renderer.createText(style || "");
        this.renderer.appendChild(styleElement, text);
        this.renderer.appendChild(this.document?.head, styleElement);
      }
    }
  }

  convertScssToCss(scssString: string): string | null {
    // Compile the SCSS string to CSS
    const result = sass.compileString(scssString);
    // Remove the newlines from the CSS
    const cssString = result.css.replace(/\n/g, ''); // Replace all newlines with empty string
    return cssString;
  }

  // Add method to handle clicked element changes
  private handleClickedElementChange(element: HTMLElement): void {
  }

  onLogout() {
    this.logout$.emit(true);
  }

}
